import React, {useEffect, useRef, useState} from "react";
import LeadInfoForm, { Slot } from "../LeadMainInfo/LeadInfoForm";
import getAvailableSpots from "../../../api/getAvailableSpots";

interface SchedulingInfoProps {
  closer?: boolean;
  setData: React.Dispatch<any>;
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
  setApiError: React.Dispatch<React.SetStateAction<boolean>>;
  setSaveRequestText: React.Dispatch<React.SetStateAction<string>>;
  data: any;
  errors: string[];
  eventId?: string;
  isCancelApt?: boolean,
  step?: number,
  cancelApt?: any,
  onSlotSelect: (isSelected: boolean, date: any, slot: Slot, addition: any) => void;
  selectedDate: any;
  selectedSlot: Slot;
}

export const groupBy = (items: any[], key: string) =>
  items?.reduce(
    (result: any, item: any) => ({
      ...result,
      [item[key]]: [...(result[item[key]] || []), item],
    }),
    {},
  );

const getFormFields = (disabled: boolean, spots?: any) => [    
  {
    label: "Select a date and time",
    component: "dateInput",
    name: "date",
    additionalClassName: "dateInput",
    options: spots || [],
  },
];

const SchedulingInfo = (props: SchedulingInfoProps): React.JSX.Element => {
  const [formFields, setFormFields] = useState(getFormFields(true));
  const [optionsForSelect, setOptionForSelect] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    props.setData((prev: any) => ({
      ...prev,
      slot: "",
      isAroSpot: null,
      spotID: null,
    }));
    
    setFormFields(getFormFields(!props.data.date?.length, optionsForSelect));
  }, [props.data.date, optionsForSelect]);

  useEffect(() => {
    if (props.data.slot) {
      const selectedDate = optionsForSelect.find(o => o.value === props.data.date);
      const appointmentInfo = selectedDate?.additions.find((a: { slot: any; }) => a.slot === props.data.slot);
      if (appointmentInfo) {
        props.setData((prev: any) => ({ 
          ...prev, 
          spotID: appointmentInfo.spotID,
          isAroSpot: appointmentInfo.isAroSpot,
          isAnytime: appointmentInfo.isAnytime
        }));
      }
    } else {
      props.setData((prev: any) => ({
        ...prev,
        isAroSpot: null,
        spotID: null,
      }));
    }
  }, [props.data.slot, optionsForSelect]);

  useEffect(() => {
    (async () => {
      if (loadingRef.current || !props.eventId) return;

      loadingRef.current = true;
      setLoadingSpots(true);
      try {
        const res = await getAvailableSpots(props.eventId, page, 5);

        if(res.data !== undefined) {
          const spots = res.data;
          const spotsForSelect = Object.entries(spots).map(([key, value]) => ({
            label: key,
            value: key,
            additions: value,
          }));

          setOptionForSelect(spotsForSelect);
          setFormFields(getFormFields(true, spotsForSelect));
          props.onSlotSelect(false, null, { start: '', end: ''}, []); // Reset the selected slot
        }
      } catch (error) {
        props.setApiError(true);
        props.setSaveRequestText('Error fetching available spots');
      } finally {
        setLoadingSpots(false);
        loadingRef.current = false;
      }

    })();
  }, [page, props.data?.appointment]);

  return (
    <LeadInfoForm
      {...props}
      formFields={formFields}
      page={page}
      setPage={setPage}
      loadingSpots={loadingSpots}
      eventId={props.eventId}
      setData={props.setData}
      data={props.data}
      onSlotSelect={props.onSlotSelect}
      selectedDate={props.selectedDate}
      selectedSlot={props.selectedSlot}
    />
  );
};

export default SchedulingInfo;